import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  useReactFlow,
  type OnConnect,
  type OnNodesChange,
  type NodeChange,
  applyNodeChanges,
  type Connection,
  BackgroundVariant,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type { SelectorTemplateDraft } from '../use-selector-template-draft';
import { useCanvasLayout } from './use-canvas-layout';
import { QuestionNode } from './question-node';
import { ProductLeafNode } from './product-leaf-node';
import { TreeNavigator } from './tree-navigator';
import { CanvasInspector } from './canvas-inspector';
import type { SelectorFlowNode } from './canvas-types';
import styles from './selector-canvas-view.module.css';

const nodeTypes = {
  question: QuestionNode,
  productLeaf: ProductLeafNode,
};

const CanvasInner = ({ state }: { state: SelectorTemplateDraft }) => {
  const { draft, nodes: selectorNodes, productsById, updateOption, handleAddNode, handleRemoveNode } = state;
  const reactFlow = useReactFlow();

  const [pinnedProductIds, setPinnedProductIds] = useState<Set<string>>(
    new Set()
  );

  const { nodes: layoutNodes, edges } = useCanvasLayout(
    draft?.tree,
    productsById,
    pinnedProductIds
  );

  const [flowNodes, setFlowNodes] = useState<SelectorFlowNode[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedProductNodeId, setSelectedProductNodeId] = useState<
    string | null
  >(null);

  // Sync layout nodes into local flow state
  useEffect(() => {
    setFlowNodes((prev) => {
      // Preserve manual positions for existing nodes
      const positionMap = new Map(prev.map((n) => [n.id, n.position]));
      return layoutNodes.map((node) => {
        const existingPos = positionMap.get(node.id);
        // Only use existing position if it came from a drag (non-zero and different from layout)
        if (existingPos && prev.some((p) => p.id === node.id)) {
          return { ...node, position: existingPos };
        }
        return node;
      });
    });
  }, [layoutNodes]);

  // fitView after initial layout
  useEffect(() => {
    if (flowNodes.length > 0) {
      setTimeout(() => reactFlow.fitView({ duration: 200 }), 50);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onNodesChange: OnNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setFlowNodes((nds) => applyNodeChanges(changes, nds) as SelectorFlowNode[]);

      // Track selection
      for (const change of changes) {
        if (change.type === 'select') {
          if (change.selected) {
            if (change.id.startsWith('product_')) {
              setSelectedProductNodeId(change.id);
              setSelectedNodeId(null);
            } else {
              setSelectedNodeId(change.id);
              setSelectedProductNodeId(null);
            }
          } else {
            if (change.id === selectedNodeId) setSelectedNodeId(null);
            if (change.id === selectedProductNodeId)
              setSelectedProductNodeId(null);
          }
        }
      }
    },
    [selectedNodeId, selectedProductNodeId]
  );

  const onConnect: OnConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.sourceHandle || !connection.target)
        return;

      const sourceNodeId = connection.source;
      const optionId = connection.sourceHandle;
      const targetId = connection.target;

      if (targetId.startsWith('product_')) {
        const productId = targetId.replace('product_', '');
        updateOption(sourceNodeId, optionId, (o) => ({
          ...o,
          next: { type: 'product', productId },
        }));
      } else {
        updateOption(sourceNodeId, optionId, (o) => ({
          ...o,
          next: { type: 'node', nodeId: targetId },
        }));
      }
    },
    [updateOption]
  );

  const onConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent, connectionState: any) => {
      // Only handle drops on empty canvas (no target)
      if (connectionState?.isValid) return;
      if (!connectionState?.fromNode?.id || !connectionState?.fromHandle?.id)
        return;

      const sourceNodeId = connectionState.fromNode.id;
      const optionId = connectionState.fromHandle.id;

      const newNodeId = handleAddNode();
      updateOption(sourceNodeId, optionId, (o) => ({
        ...o,
        next: { type: 'node', nodeId: newNodeId },
      }));
    },
    [handleAddNode, updateOption]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const productId = event.dataTransfer.getData('application/product-id');
      if (!productId) return;

      setPinnedProductIds((prev) => {
        const next = new Set(prev);
        next.add(productId);
        return next;
      });
    },
    []
  );

  const handleNavigatorSelect = useCallback(
    (nodeId: string) => {
      setSelectedNodeId(nodeId);
      setSelectedProductNodeId(null);
      // Select the node on the canvas
      setFlowNodes((nds) =>
        nds.map((n) => ({
          ...n,
          selected: n.id === nodeId,
        }))
      );
    },
    []
  );

  // Delete selected node on Delete/Backspace key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (draft?.isArchived) return;
      // Don't intercept if user is typing in an input
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedNodeId) {
          e.preventDefault();
          handleRemoveNode(selectedNodeId);
          setSelectedNodeId(null);
        } else if (selectedProductNodeId) {
          e.preventDefault();
          const productId = selectedProductNodeId.replace('product_', '');
          // Unpin the product
          setPinnedProductIds((prev) => {
            const next = new Set(prev);
            next.delete(productId);
            return next;
          });
          // Disconnect any options pointing to this product
          if (draft) {
            for (const node of Object.values(draft.tree.nodes)) {
              for (const option of node.options) {
                if (
                  option.next.type === 'product' &&
                  option.next.productId === productId
                ) {
                  updateOption(node.id, option.id, (o) => ({
                    ...o,
                    next: { type: 'none' },
                  }));
                }
              }
            }
          }
          setSelectedProductNodeId(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNodeId, selectedProductNodeId, draft, handleRemoveNode, updateOption]);

  if (!draft) return null;

  return (
    <div className={styles.canvasLayout}>
      <TreeNavigator
        nodes={selectorNodes}
        rootNodeId={draft.tree.rootNodeId}
        selectedNodeId={selectedNodeId}
        onSelectNode={handleNavigatorSelect}
        onAddNode={() => handleAddNode()}
        disabled={draft.isArchived}
      />
      <div className={styles.canvasContainer}>
        <ReactFlow
          nodes={flowNodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onConnect={onConnect}
          onConnectEnd={onConnectEnd}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onPaneClick={() => {
            setSelectedNodeId(null);
            setSelectedProductNodeId(null);
          }}
          fitView
          deleteKeyCode={null}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
          <Controls showInteractive={false} />
          <MiniMap
            nodeStrokeWidth={3}
            pannable
            zoomable
          />
        </ReactFlow>
      </div>
      <CanvasInspector
        state={state}
        selectedNodeId={selectedNodeId}
        selectedProductNodeId={selectedProductNodeId}
      />
    </div>
  );
};

export const SelectorCanvasView = ({
  state,
}: {
  state: SelectorTemplateDraft;
}) => {
  return (
    <ReactFlowProvider>
      <CanvasInner state={state} />
    </ReactFlowProvider>
  );
};
